import {
  Delete,
  Download,
  Edit,
  FileCopy,
  Info,
  Visibility,
} from '@mui/icons-material';
import {
  Button,
  Divider,
  Grid,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { deleteWorkspaceFile, FileItem, updateWorkspaceFile } from 'api/files';
import { useCurrentModal, useModal } from 'components/modal';
import { useNotifications } from 'components/notify';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { formatFileSize } from '../utils/formatFileSize';

type FileMenuProps = {
  file: FileItem | null;
  onDelete?: () => void;
  // New props for context menu
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onPreview?: () => void;
};

export default function FileMenu({
  file,
  onDelete,
  anchorEl,
  onPreview,
  onClose: onContextMenuClose,
}: FileMenuProps) {
  const { workspaceId } = useParams();
  const { showNotification } = useNotifications();

  const { showModal } = useModal();

  const handleDeleteFile = async () => {
    if (!workspaceId || !file) return;

    try {
      await deleteWorkspaceFile(workspaceId, file.id);
      showNotification('File deleted successfully', {
        variant: 'success',
      });
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
      showNotification('Failed to delete file', {
        variant: 'error',
      });
    } finally {
      onContextMenuClose();
    }
  };

  const handleCopyLink = async () => {
    if (!workspaceId || !file) return;

    const s3Url = file.url;

    try {
      await navigator.clipboard.writeText(s3Url);
      showNotification('Link copied to clipboard', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Failed to copy link:', error);
      showNotification('Failed to copy link', {
        variant: 'error',
      });
    }
    onContextMenuClose();
  };

  const handleEditPath = () => {
    if (!workspaceId || !file) return;

    const EditPathModal = () => {
      const modalCtx = useCurrentModal();
      const [pathValue, setPathValue] = useState(file?.path || []);
      const [isSaving, setIsSaving] = useState(false);

      const handleSave = async () => {
        if (!workspaceId || !file) return;

        setIsSaving(true);
        try {
          await updateWorkspaceFile(workspaceId, file.id, {
            path: pathValue || [],
          });
          showNotification('Path updated successfully', {
            variant: 'success',
          });
          modalCtx?.closeModal();
          onContextMenuClose();
        } catch (error) {
          console.error('Failed to update path:', error);
          showNotification('Failed to update path', {
            variant: 'error',
          });
        } finally {
          setIsSaving(false);
        }
      };

      return (
        <Stack spacing={2} sx={{ minWidth: 400 }}>
          <TextField
            label="Path"
            value={pathValue.join('/')}
            onChange={(e) => setPathValue(e.target.value.split('/'))}
            fullWidth
            placeholder="Enter file path (optional)"
            helperText="Leave empty to clear the path"
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => modalCtx?.closeModal()}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      );
    };

    showModal('Edit Path - ' + (file?.name || ''), <EditPathModal />);
    onContextMenuClose();
  };

  return (
    <Menu
      open={Boolean(anchorEl && file)}
      onClose={onContextMenuClose}
      anchorEl={anchorEl}
    >
      <MenuItem onClick={onPreview} disabled={!onPreview}>
        <ListItemIcon>
          <Visibility fontSize="small" />
        </ListItemIcon>
        Preview
      </MenuItem>
      <Divider />
      <MenuItem
        onClick={() => {
          if (file?.url) {
            window.open(file.url, '_blank');
          }
          onContextMenuClose();
        }}
      >
        <ListItemIcon>
          <Download fontSize="small" />
        </ListItemIcon>
        Download
      </MenuItem>
      <MenuItem onClick={handleCopyLink}>
        <ListItemIcon>
          <FileCopy fontSize="small" />
        </ListItemIcon>
        Copy Link
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleEditPath}>
        <ListItemIcon>
          <Edit fontSize="small" />
        </ListItemIcon>
        Edit Path
      </MenuItem>
      <MenuItem
        onClick={() => {
          showModal(
            'File Info - ' + (file?.name || ''),
            <Stack maxWidth={500}>
              <Grid container>
                <PropertyItem name="Name" value={file?.name || ''} />
                <PropertyItem
                  name="Size"
                  value={formatFileSize(+(file?.size ?? '0'))}
                />
                <PropertyItem name="Path" value={file?.path?.join('/') || ''} />
                <Grid item xs={12} py={1}>
                  <Divider />
                </Grid>
                <PropertyItem
                  name="Created At"
                  value={file?.created_at || ''}
                />
                <PropertyItem
                  name="Modified At"
                  value={file?.modified_at || ''}
                />
                <Grid item xs={12} py={1}>
                  <Divider />
                </Grid>
                <PropertyItem name="Status" value={file?.status || ''} />
                <PropertyItem name="URL" value={file?.url || ''} />
                <Grid item xs={12} py={1}>
                  <Divider />
                </Grid>
                <PropertyItem name="ID" value={file?.id || ''} />
                <PropertyItem
                  name="Workspace ID"
                  value={file?.workspace_id || ''}
                />
                <PropertyItem
                  name="S3 Upload ID"
                  value={file?.s3_upload_id || ''}
                />
              </Grid>
            </Stack>,
          );
        }}
      >
        <ListItemIcon>
          <Info fontSize="small" />
        </ListItemIcon>
        Get Info
      </MenuItem>
      <Divider />
      <MenuItem
        disabled={!onDelete}
        onClick={handleDeleteFile}
        sx={{ color: 'error.main' }}
      >
        <ListItemIcon>
          <Delete fontSize="small" sx={{ color: 'error.main' }} />
        </ListItemIcon>
        Delete
      </MenuItem>
    </Menu>
  );
}

function PropertyItem(props: { name: string; value: string }) {
  return (
    <>
      <Grid item xs={3}>
        <Typography>{props.name}:</Typography>
      </Grid>
      <Grid item xs={9}>
        <Typography>{props.value}</Typography>
      </Grid>
    </>
  );
}
